import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import {
  listOwnerPeople,
  type OwnerPersonListItem,
  type PeopleSavedViewId,
} from '../../../services/peopleService'
import { OwnerPersonDetail } from './OwnerPersonDetail'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import { useNls } from '../../../nls/nlsProvider'
import './ownerPeople.css'

interface SavedViewItem {
  id: PeopleSavedViewId
  label: string
}

const SAVED_VIEWS: SavedViewItem[] = [
  { id: 'all', label: 'All users' },
  { id: 'new-signups', label: 'New signups this week' },
  { id: 'power-users', label: 'Free-plan power users' },
  { id: 'users-with-restrictions', label: 'Users with restrictions' },
]

const VALID_SORTS = ['lastActiveAt', 'createdAt', 'openTicketCount', 'creditSpend'] as const
type OwnerPeopleSortBy = (typeof VALID_SORTS)[number]
const isOwnerPeopleSortBy = (val: unknown): val is OwnerPeopleSortBy =>
  typeof val === 'string' && (VALID_SORTS as readonly string[]).includes(val)

export interface OwnerPeoplePanelProps {
  route: OwnerRoute
}

function OwnerPeopleList({ route }: OwnerPeoplePanelProps) {
  const { t } = useNls()

  const query = useMemo(() => route.query || {}, [route.query])
  const activeSavedView = (query.savedView as PeopleSavedViewId) || 'all'
  const activePlan = query.plan || 'ALL'
  const activeHasTickets = query.hasOpenTickets === 'true'
  const activeHasRestrictions = query.hasRestrictions === 'true'
  const activeHasCredits = query.hasCredits === 'true'
  const activeSearch = query.q || ''
  const activeSort: OwnerPeopleSortBy = isOwnerPeopleSortBy(query.sortBy) ? query.sortBy : 'lastActiveAt'

  const [people, setPeople] = useState<OwnerPersonListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copiedUid, setCopiedUid] = useState<string | null>(null)
  const [openMenuUid, setOpenMenuUid] = useState<string | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [cursorFilterKey, setCursorFilterKey] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageCursors, setPageCursors] = useState<Array<string | null>>([null])
  const [metadata, setMetadata] = useState<{ orderingChanged?: boolean; boundedSearch?: { cap: number; truncated: boolean; prefixComplete?: boolean } | null }>({})
  const requestVersion = useRef(0)

  // URL query updater
  const updateQuery = useCallback(
    (patch: Record<string, string | undefined>) => {
      const nextQuery: Record<string, string | undefined> = { ...query, ...patch }
      Object.keys(nextQuery).forEach((k) => {
        if (nextQuery[k] === undefined || nextQuery[k] === '') {
          delete nextQuery[k]
        }
      })
      navigateOwner({ view: 'people', query: nextQuery })
    },
    [query],
  )

  const filterKey = `${activeSearch}|${activePlan}|${activeSavedView}|${activeHasTickets}|${activeHasRestrictions}|${activeHasCredits}|${activeSort}`

  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey)
    setCursor(null)
    setCursorFilterKey(filterKey)
    setNextCursor(null)
    setPageNumber(1)
    setPageCursors([null])
    setLoading(true)
    setError(null)
  }

  // Effects reset after render. Gate the request cursor by its originating
  // filter key so a changed filter can never be sent with the previous page.
  const requestCursor = cursorFilterKey === filterKey ? cursor : null

  useEffect(() => {
    let active = true
    const version = ++requestVersion.current
    void listOwnerPeople({
      search: activeSearch || undefined,
      plan: activePlan !== 'ALL' ? activePlan : undefined,
      savedView: activeSavedView,
      hasOpenTickets: activeHasTickets || undefined,
      hasRestrictions: activeHasRestrictions || undefined,
      hasCredits: activeHasCredits || undefined,
      sortBy: activeSort,
      sortDir: 'desc',
      cursor: requestCursor,
      limit: 50,
    })
      .then((res) => {
        if (!active || version !== requestVersion.current) return
        setPeople(res.people)
        setNextCursor(res.nextCursor)
        setMetadata({ orderingChanged: res.orderingChanged, boundedSearch: res.boundedSearch })
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (!active || version !== requestVersion.current) return
        const message = err instanceof Error ? err.message : 'Failed to fetch people list'
        setError(message)
        setLoading(false)
      })
    return () => {
      active = false
    }
  }, [activeSearch, activePlan, activeSavedView, activeHasTickets, activeHasRestrictions, activeHasCredits, activeSort, requestCursor])

  const handleCopyUid = (uid: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    navigator.clipboard?.writeText(uid)
    setCopiedUid(uid)
    setTimeout(() => setCopiedUid(null), 2000)
    setOpenMenuUid(null)
  }

  const handleRowClick = (person: OwnerPersonListItem) => {
    navigateOwner({ view: 'person-detail', userId: person.uid })
  }

  const columns: ColumnDef<OwnerPersonListItem>[] = useMemo(
    () => [
      {
        key: 'person',
        header: 'Person',
        render: (row) => {
          const initial = (row.email?.[0] || row.displayName?.[0] || 'U').toUpperCase()
          const joinedFormatted = row.createdAt
            ? new Date(row.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : 'Unknown'
          const lastActiveFormatted = row.lastActiveAt
            ? new Date(row.lastActiveAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : 'Never'

          return (
            <div className="ownerPeopleRowMain">
              <div className="ownerPeopleAvatar">{initial}</div>
              <div className="ownerPeopleRowText">
                <span className="ownerPeopleEmailPrimary">{row.email}</span>
                <span className="ownerPeopleMetaSecondary">
                  {row.displayName || 'No display name'} · joined {joinedFormatted} · last active {lastActiveFormatted}
                </span>
              </div>
            </div>
          )
        },
      },
      {
        key: 'plan',
        header: 'Plan',
        width: 100,
        render: (row) => (
          <span className={`ownerPeoplePlanPill ownerPeoplePlanPill--${row.plan.toLowerCase()}`}>
            {row.plan}
          </span>
        ),
      },
      {
        key: 'workspaces',
        header: 'Workspaces',
        width: 110,
        render: (row) => (
          <span style={{ fontSize: 13, color: 'var(--text-hi)' }}>
            {row.workspacesOwnedCount} {row.workspacesOwnedCount === 1 ? 'workspace' : 'workspaces'}
          </span>
        ),
      },
      {
        key: 'credits',
        header: 'Credits',
        width: 110,
        render: (row) => (
          <span style={{ fontSize: 13, color: 'var(--text-hi)' }}>
            {row.credits.personalAvailable.toLocaleString()} Cr
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: 140,
        render: (row) => (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {row.openTicketCount > 0 && (
              <span className="ownerPeopleTicketPill">{row.openTicketCount} ticket{row.openTicketCount > 1 ? 's' : ''}</span>
            )}
            {row.hasRestrictions && (
              <span className="ownerPeopleRestrictedPill">Restricted</span>
            )}
            {row.openTicketCount === 0 && !row.hasRestrictions && (
              <span style={{ fontSize: 12, color: 'var(--text-low)' }}>—</span>
            )}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        render: (row) => {
          const isOpen = openMenuUid === row.uid
          return (
            <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="ownerPeopleActionsBtn"
                onClick={() => setOpenMenuUid(isOpen ? null : row.uid)}
                title="More actions"
              >
                <MoreHorizRoundedIcon fontSize="small" />
              </button>
              {isOpen && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    background: 'var(--surface-2, #1a2029)',
                    border: '1px solid var(--divider, #232b36)',
                    borderRadius: 6,
                    padding: 4,
                    zIndex: 50,
                    minWidth: 140,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  }}
                >
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-hi)',
                      padding: '6px 10px',
                      fontSize: 12,
                      cursor: 'pointer',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    onClick={() => handleCopyUid(row.uid)}
                  >
                    {copiedUid === row.uid ? <CheckRoundedIcon style={{ fontSize: 13, color: 'var(--ok)' }} /> : <ContentCopyRoundedIcon style={{ fontSize: 13 }} />}
                    Copy UID
                  </button>
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-hi)',
                      padding: '6px 10px',
                      fontSize: 12,
                      cursor: 'pointer',
                      borderRadius: 4,
                    }}
                    onClick={() => navigateOwner({ view: 'person-detail', userId: row.uid })}
                  >
                    View details
                  </button>
                </div>
              )}
            </div>
          )
        },
      },
    ],
    [copiedUid, openMenuUid],
  )

  return (
    <div className="ownerPeopleContainer" data-testid="owner-people-panel">
      {/* Top Header */}
      <div className="ownerPeopleHeader">
        <div className="ownerPeopleHeaderTop">
          <div className="ownerPeopleTitleArea">
            <h1 className="ownerPeopleTitle">People Hub</h1>
            <span className="ownerPeopleCountBadge">{people.length} users</span>
          </div>

          {/* Saved Views Tabs */}
          <nav className="ownerPeopleSavedViewTabs" aria-label="People saved views">
            {SAVED_VIEWS.map((sv) => (
              <button
                key={sv.id}
                type="button"
                className={`ownerPeopleSavedViewTab ${activeSavedView === sv.id ? 'ownerPeopleSavedViewTab--active' : ''}`}
                onClick={() => updateQuery({ savedView: sv.id === 'all' ? undefined : sv.id })}
              >
                {sv.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Filter Bar */}
        <div className="ownerPeopleFilterBar">
          <div className="ownerPeopleSearchInputWrap">
            <input
              type="text"
              className="ownerPeopleSearchInput"
              placeholder="Search by email, name, or UID prefix…"
              value={activeSearch}
              onChange={(e) => updateQuery({ q: e.target.value || undefined })}
            />
            {metadata.boundedSearch?.truncated && (
              <span className="ownerPeopleSearchHint" style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 4, display: 'block' }}>
                {metadata.boundedSearch.prefixComplete
                  ? `Search reached its ${metadata.boundedSearch.cap}-user substring window.`
                  : `Search reached its ${metadata.boundedSearch.cap}-user candidate window; UID prefix results may be incomplete.`}
              </span>
            )}
            {metadata.orderingChanged && (
              <span className="ownerPeopleSearchHint" style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 4, display: 'block' }}>
                A filter-required ordering is primary; your selected sort is secondary.
              </span>
            )}
          </div>

          <div className="ownerPeopleFilterChips">
            {/* Plan filter */}
            <select
              className="ownerPeopleSortSelect"
              value={activePlan}
              onChange={(e) => updateQuery({ plan: e.target.value === 'ALL' ? undefined : e.target.value })}
            >
              <option value="ALL">All plans</option>
              <option value="FREE">Free</option>
              <option value="PRO">Pro</option>
              <option value="GROWTH">Growth</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>

            {/* Has open tickets chip */}
            <button
              type="button"
              className={`ownerPeopleChip ${activeHasTickets ? 'ownerPeopleChip--active' : ''}`}
              onClick={() => updateQuery({ hasOpenTickets: activeHasTickets ? undefined : 'true' })}
            >
              Has open tickets
            </button>

            {/* Has restrictions chip */}
            <button
              type="button"
              className={`ownerPeopleChip ${activeHasRestrictions ? 'ownerPeopleChip--active' : ''}`}
              onClick={() => updateQuery({ hasRestrictions: activeHasRestrictions ? undefined : 'true' })}
            >
              Has restrictions
            </button>

            {/* Non-zero credits chip */}
            <button
              type="button"
              className={`ownerPeopleChip ${activeHasCredits ? 'ownerPeopleChip--active' : ''}`}
              onClick={() => updateQuery({ hasCredits: activeHasCredits ? undefined : 'true' })}
            >
              Has personal credits
            </button>

            {/* Sort selector */}
            <select
              className="ownerPeopleSortSelect"
              value={activeSort}
              onChange={(e) => updateQuery({ sortBy: e.target.value })}
            >
              <option value="lastActiveAt">Sort: Last active</option>
              <option value="createdAt">Sort: Joined date</option>
              <option value="openTicketCount">Sort: Open tickets</option>
              <option value="creditSpend">Sort: Credit balance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table Pane */}
      <div className="ownerPeopleListPane">
        {loading ? (
          <div style={{ padding: 32, color: 'var(--text-mid)' }}>Loading people…</div>
        ) : error ? (
          <div style={{ padding: 32, color: 'var(--danger)' }}>{error}</div>
        ) : people.length === 0 ? (
          <OwnerEmptyScaffold
            title="No people match these filters"
            body="Try clearing your search query or loosening the selected filter chips."
            explanation="The people index synchronizes user registrations and activities across workspaces."
          />
        ) : (
          <>
            <OwnerTable
              data={people}
              columns={columns}
              viewKey="owner-people"
              getRowId={(row) => row.uid}
              onRowClick={handleRowClick}
              pageSize={100}
            />
          </>
        )}
        {!loading && !error && (people.length > 0 || nextCursor || pageNumber > 1) && (
            <div className="ownerPeoplePagination" aria-label="People server pagination">
              <span>{t('NLSOwnerConsolePeoplePage', { page: pageNumber })}</span>
              <button type="button" disabled={pageNumber === 1 || loading} onClick={() => {
                const priorCursor = pageCursors[pageNumber - 2]
                setCursor(priorCursor)
                setCursorFilterKey(filterKey)
                setPageNumber((page) => page - 1)
              }}>{t('NLSOwnerConsolePrevious')}</button>
              <button type="button" disabled={!nextCursor || loading} onClick={() => {
                if (!nextCursor) return
                setCursor(nextCursor)
                setCursorFilterKey(filterKey)
                setPageCursors((history) => [...history.slice(0, pageNumber), nextCursor])
                setPageNumber((page) => page + 1)
              }}>{t('NLSOwnerConsolePeopleNextPage')}</button>
            </div>
        )}
      </div>
    </div>
  )
}

export function OwnerPeoplePanel({ route }: OwnerPeoplePanelProps) {
  if (route.view === 'person-detail' && route.userId) {
    return <OwnerPersonDetail userId={route.userId} />
  }

  return <OwnerPeopleList route={route} />
}
